import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDeleteOperatorLogoMutation,
  useEditOperatorMutation,
  useOperatorLogoQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  useUploadOperatorLogoMutation,
} from '@common/cache';
import { paths } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { dataURItoBlob } from '@util/image-utils';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';
import { InferType } from 'yup';

type IGeneralFormShape = InferType<typeof generalSchema>;

const initialValues: IGeneralFormShape = {
  name: '',
  organization_id: '',
  partner_id: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
};

export const useGeneral = (
  operator: paths[`/operator/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync, isPending } = useEditOperatorMutation(operator.id);
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();

  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(generalSchema) as any,
    defaultValues: {
      ...initialValues,
      ...operator,
      ...operator.address,
      organization_id: operator.organization.id,
      partner_id: operator.partner.id,
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = formMethods;

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });

  const { data: partnerOptions = [], refetch } = usePartnerShortQuery(
    org_id as string,
  );

  useEffect(() => {
    if (org_id) {
      refetch();
    }
  }, [org_id]);

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const fetchedLogo = useOperatorLogoQuery({
    operator_id: operator.id,
  });
  const [logo, setLogo] = useState<string>(
    fetchedLogo.data ? URL.createObjectURL(fetchedLogo?.data) : '',
  );

  useEffect(() => {
    if (fetchedLogo.data) {
      setLogo(URL.createObjectURL(fetchedLogo.data as Blob));
    }
  }, [fetchedLogo.data]);

  const uploadLogoMutation = useUploadOperatorLogoMutation(operator.id);

  const handleUploadLogo = async (newLogo: string) => {
    setLogo(newLogo);

    let blob = dataURItoBlob(newLogo);
    let fd = new FormData();
    fd.append('file', blob);

    try {
      toast.info('Logo uploading...');

      await uploadLogoMutation.mutateAsync({
        id: operator.id,
        file: fd,
      });
      toast.success('Logo updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteLogoMutation = useDeleteOperatorLogoMutation(operator.id);

  const handleDeleteLogo = () => {
    deleteLogoMutation.mutateAsync({ id: operator.id });
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...values,
        operator_id: operator.id,
        country: 'US',
      });

      toast.success('Changes saved');
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  return {
    formMethods,
    onSubmit,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    setValue,
    isPartnerSelectDisabled: organizationOptions.length > 0 && !org_id,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
